type JsonObject = Record<string, unknown>;

export type WhatsAppConnectionFormState = {
  selfChatMode: boolean;
  sendReadReceipts: boolean;
  blockStreaming: boolean;
  configWrites: boolean;
  defaultTo: string;
  messagePrefix: string;
  responsePrefix: string;
  debounceMs: string;
  mediaMaxMb: string;
  chunkMode: string;
  textChunkLimit: string;
};

const PRESERVED_EXISTING_KEYS = new Set([
  "dmPolicy",
  "allowFrom",
  "groupPolicy",
  "groupAllowFrom",
  "ackReaction",
  "heartbeat",
  "healthMonitor",
  "dms",
]);

function isRecord(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function readBoolean(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

export function createDefaultWhatsAppConnectionFormState(): WhatsAppConnectionFormState {
  return {
    selfChatMode: false,
    sendReadReceipts: false,
    blockStreaming: false,
    configWrites: false,
    defaultTo: "",
    messagePrefix: "",
    responsePrefix: "",
    debounceMs: "",
    mediaMaxMb: "",
    chunkMode: "",
    textChunkLimit: "",
  };
}

export function parseWhatsAppConnectionConfig(
  config: unknown,
): WhatsAppConnectionFormState {
  const base = createDefaultWhatsAppConnectionFormState();
  if (!isRecord(config)) {
    return base;
  }

  return {
    selfChatMode: readBoolean(config.selfChatMode),
    sendReadReceipts: readBoolean(config.sendReadReceipts),
    blockStreaming: readBoolean(config.blockStreaming),
    configWrites: readBoolean(config.configWrites),
    defaultTo: readString(config.defaultTo),
    messagePrefix: readString(config.messagePrefix),
    responsePrefix: readString(config.responsePrefix),
    debounceMs:
      typeof config.debounceMs === "number" && Number.isFinite(config.debounceMs)
        ? String(config.debounceMs)
        : "",
    mediaMaxMb:
      typeof config.mediaMaxMb === "number" && Number.isFinite(config.mediaMaxMb)
        ? String(config.mediaMaxMb)
        : "",
    chunkMode: readString(config.chunkMode),
    textChunkLimit:
      typeof config.textChunkLimit === "number" && Number.isFinite(config.textChunkLimit)
        ? String(config.textChunkLimit)
        : "",
  };
}

export function buildWhatsAppConnectionConfig(
  state: WhatsAppConnectionFormState,
  existingConfig?: unknown,
): JsonObject {
  // Preserve domain config that is not currently exposed in the form.
  const preserved: JsonObject = {};
  if (isRecord(existingConfig)) {
    for (const [key, value] of Object.entries(existingConfig)) {
      if (PRESERVED_EXISTING_KEYS.has(key) && value !== undefined && value !== null) {
        preserved[key] = value;
      }
    }
  }

  const config: JsonObject = { ...preserved };

  const assignString = (key: string, value: string) => {
    const trimmed = value.trim();
    if (trimmed) {
      config[key] = trimmed;
    }
  };

  const assignNumber = (key: string, value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {return;}
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) {
      throw new Error(`${key} must be a valid non-negative number.`);
    }
    config[key] = parsed;
  };

  if (state.selfChatMode) {config.selfChatMode = true;}
  if (state.sendReadReceipts) {config.sendReadReceipts = true;}
  if (state.blockStreaming) {config.blockStreaming = true;}
  if (state.configWrites) {config.configWrites = true;}
  assignString("defaultTo", state.defaultTo);
  assignString("messagePrefix", state.messagePrefix);
  assignString("responsePrefix", state.responsePrefix);
  assignNumber("debounceMs", state.debounceMs);
  assignNumber("mediaMaxMb", state.mediaMaxMb);
  assignString("chunkMode", state.chunkMode);
  assignNumber("textChunkLimit", state.textChunkLimit);

  return config;
}
